import { z } from "zod";

/**
 * DTO cho Group Financial Health + Config-Audit (US-AE4.F4-0017 · @UC9033 group-financial-health ·
 * @UC9034 group-config-audit). Một nguồn sự thật cho api (S01 metrics / S02 config-audit query +
 * redaction theo vai) và admin web (S04 render) — tránh drift shape.
 *
 * BẢO MẬT / INVARIANTS:
 *  - B1: tiền = BigInt minor units serialize thành STRING, KHÔNG float. Field redact-được (số tiền)
 *    = NULLABLE — vai thấp (SUPPORT/READ_ONLY) không xem (mirror redaction matrix F1 US-AE4.F1-0015 §6).
 *  - C2: config-audit before/after lấy từ audit_log.diff ĐÃ redact ở nguồn (redact-diff.ts) — DTO
 *    chỉ mang giá trị an toàn; KHÔNG bao giờ chứa secret/PII thô. `.strict()` reject field lạ.
 *  - AC2: flags[] CHỈ mang KẾT QUẢ cờ (code/severity/metric) — KHÔNG hardcode ngưỡng số. Ngưỡng
 *    config-driven ở backend (SystemSetting); chưa set ⇒ flags rỗng.
 *  - Latency dưới dạng {value, unit} (giờ|ngày) — số nguyên, KHÔNG float tiền.
 *  - Envelope config-audit khớp quy ước repo `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size config-audit. Default (20) do backend áp khi vắng. */
export const ADMIN_GROUP_CONFIG_AUDIT_MAX_LIMIT = 50;

/** Đơn vị độ trễ — số nguyên value đi kèm. */
export const groupHealthLatencyUnitSchema = z.enum(["hours", "days"]);
export type GroupHealthLatencyUnit = z.infer<typeof groupHealthLatencyUnitSchema>;

/**
 * Độ trễ = {value: số nguyên ≥ 0, unit}. NULL khi không có mẫu (chưa có payment/expense đã xử lý).
 * value là NUMBER (đơn vị thời gian — giờ/ngày), KHÔNG phải tiền ⇒ number hợp lệ (B1 chỉ cấm tiền float).
 */
export const groupHealthLatencySchema = z
  .object({
    value: z.number().int().min(0),
    unit: groupHealthLatencyUnitSchema,
  })
  .strict();
export type GroupHealthLatency = z.infer<typeof groupHealthLatencySchema>;

/** Mức độ cờ cảnh báo (AC2). */
export const groupHealthFlagSeveritySchema = z.enum(["warn", "info"]);
export type GroupHealthFlagSeverity = z.infer<typeof groupHealthFlagSeveritySchema>;

/**
 * Cờ vấn đề (AC2) — 1 chỉ số vượt NGƯỠNG (config-driven). code ổn định để i18n; metric = tên chỉ số
 * liên quan. KHÔNG mang giá trị ngưỡng số (index §6 — không bịa số).
 */
export const groupHealthFlagSchema = z
  .object({
    code: z.string(),
    severity: groupHealthFlagSeveritySchema,
    metric: z.string(),
  })
  .strict();
export type GroupHealthFlag = z.infer<typeof groupHealthFlagSchema>;

/**
 * Sức khoẻ tài chính nhóm (AC1 + AC2). 4 chỉ số đọc-thuần từ nguồn sự thật:
 *  - totalBalanceMinor: Σ AccountBalance qua các fund (đọc ledger — KHÔNG tính từ lines). null khi redact.
 *  - avgDuesPerMemberMinor: Σ dues tồn đọng / #member active. null khi redact hoặc không có member.
 *  - paymentConfirmationLatency: mean (createdAt→reviewedAt) PaymentConfirmation approved. null nếu không có mẫu.
 *  - expenseProcessingLatency: mean (createdAt→posted) Expense. null nếu không có mẫu.
 *  - flags: cờ vượt ngưỡng (rỗng khi không vượt / ngưỡng chưa set).
 */
export const groupHealthSchema = z
  .object({
    groupId: z.string(),
    /** BigInt minor units as string; null khi redact (vai thấp). KHÔNG number. */
    totalBalanceMinor: z.string().nullable(),
    /** BigInt minor units as string; null khi redact hoặc không có member active. KHÔNG number. */
    avgDuesPerMemberMinor: z.string().nullable(),
    paymentConfirmationLatency: groupHealthLatencySchema.nullable(),
    expenseProcessingLatency: groupHealthLatencySchema.nullable(),
    flags: z.array(groupHealthFlagSchema),
  })
  .strict();
export type GroupHealth = z.infer<typeof groupHealthSchema>;

/**
 * Query config-audit: cursor opaque + limit ∈ [1,50] optional. Keyset (createdAt DESC, id DESC).
 */
export const adminGroupConfigAuditQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_GROUP_CONFIG_AUDIT_MAX_LIMIT).optional(),
});
export type AdminGroupConfigAuditQuery = z.infer<typeof adminGroupConfigAuditQuerySchema>;

/**
 * Một entry config-audit (AC3 — ai/gì/cũ-mới/khi-nào) = view của audit_log lọc config-change nhóm/quỹ.
 *  - actor: actorUserId (id admin/user) — có thể null (audit hệ thống).
 *  - action/entity/entityId: nguồn thay đổi (group | fund).
 *  - target: khoá settings liên quan (vd 'fund.config'); rút từ action (KHÔNG lộ PII).
 *  - before/after: từ audit_log.diff ĐÃ redact (unknown — nullable). Có thể null nếu diff không tách được.
 *  - createdAt: khi-nào (ISO).
 * `.strict()` — KHÔNG field lạ (chống leak).
 */
export const adminGroupConfigAuditItemSchema = z
  .object({
    id: z.string(),
    actor: z.string().nullable(),
    action: z.string(),
    entity: z.enum(["group", "fund"]),
    entityId: z.string().nullable(),
    target: z.string(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type AdminGroupConfigAuditItem = z.infer<typeof adminGroupConfigAuditItemSchema>;

/** Envelope config-audit khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminGroupConfigAuditResponseSchema = z.object({
  data: z.array(adminGroupConfigAuditItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});
export type AdminGroupConfigAuditResponse = z.infer<typeof adminGroupConfigAuditResponseSchema>;
